"use client";
import { useState, type FormEvent } from "react";
import { createLeadSubmitter } from "@/lib/lead-submit";
import { WEEKLY_CONSENT } from "@/lib/customy/forms-contract";
import { t, type Locale } from "@/lib/i18n";
import { agentFormAttributes, agentFieldAttributes, respondToAgent } from "@/lib/agentic/forms";

/** `variant="v2"` renders the design-system v2 card (home page); the default keeps the legacy markup. */
export function BriefSignup({ locale = "es", variant = "legacy", topic, id = "newsletter" }: { locale?: Locale; variant?: "legacy" | "v2"; topic?: string; id?: string }) {
  const v2 = variant === "v2";
  const copy = t(locale).brief;
  const [sendLead] = useState(() => createLeadSubmitter());
  const [status, setStatus] = useState<"idle" | "pending" | "done" | "error">(
    "idle",
  );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "pending" || status === "done") return;
    const form = new FormData(event.currentTarget);
    setStatus("pending");
    try {
      if (form.get("consent") !== "on") throw new Error("Consent required");
      const submission = sendLead({
        email: String(form.get("email") ?? ""),
        consent: true,
        source: "codifly-overview",
        // A topic page (e.g. AI model costs) tags the subscriber with its category for segmentation.
        ...(topic ? { category: topic } : {}),
        website: String(form.get("website") ?? ""),
      });
      respondToAgent(event, submission, { received: copy.received, error: copy.error });
      await submission;
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }
  return (
    <section className={v2 ? "hv-brief" : "cf-newsletter"} id={id}>
      <div>
        <span className={v2 ? "ui-eyebrow" : "cf-eyebrow"}>{copy.eyebrow}</span>
        <h2>
          {copy.title}
          <br />
          {copy.titleBreak}
        </h2>
        <p>{copy.lead}</p>
      </div>
      <form {...agentFormAttributes("newsletter", locale, id)} onSubmit={submit} className={v2 ? "hv-brief-form" : undefined}>
        <div hidden aria-hidden="true">
          <label>
            Website
            <input {...agentFieldAttributes("website", locale)} name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <label htmlFor={`${id}-email`}>{copy.email}</label>
        <div className={v2 ? "hv-brief-row" : "cf-email-row"}>
          <input
            id={`${id}-email`}
            name="email"
            {...agentFieldAttributes("email", locale)}
            type="email"
            placeholder={copy.placeholder}
            className={v2 ? "ui-input" : undefined}
            required
            maxLength={200}
          />
          <button
            className={v2 ? "ui-btn ui-btn-brand ui-btn-lg" : undefined}
            type="submit"
            disabled={status === "pending" || status === "done"}
          >
            {status === "pending"
              ? copy.sending
              : status === "done"
                ? copy.done
                : copy.subscribe}
          </button>
        </div>
        <label className={v2 ? "hv-brief-consent" : "cf-consent"}>
          <input {...agentFieldAttributes("consent", locale)} type="checkbox" name="consent" required />
          {/* The Customy form records WEEKLY_CONSENT (Spanish, versioned); other languages show its translation. */}
          {locale === "es" ? WEEKLY_CONSENT : copy.consent}
        </label>
        <p role="status" className={v2 ? "hv-brief-status" : undefined}>
          {status === "done"
            ? copy.received
            : status === "error"
              ? copy.error
              : copy.unsubscribe}
        </p>
      </form>
    </section>
  );
}
